import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null}
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState);
  }

  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Handles quick add button click
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);

      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (!productGrid) {
      console.warn('Quick add: [data-product-grid-content] not found for', currentUrl);
      return;
    }

    await this.updateQuickAddModal(productGrid);
    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();

    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * @param {string} productPageUrl
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      return new DOMParser().parseFromString(responseText, 'text/html');
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      }
      throw error;
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {Element} productGrid
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    const content = /** @type {Element} */ (productGrid.cloneNode(true));

    const scripts = content.querySelectorAll('script');
    scripts.forEach((script) => script.remove());

    modalContent.innerHTML = '';
    modalContent.appendChild(content);

    this.#initCustomQuickAdd(modalContent);
    this.#syncVariantSelection(modalContent);
  }

  /**
   * Initializes your custom quick add UI inside the modal
   * @param {Element} modalContent
   */
  #initCustomQuickAdd(modalContent) {
    const wrapper = modalContent.querySelector('[id^="ProductInformation-"]');
    if (!wrapper) return;

    const variantButtons = wrapper.querySelectorAll('.variant-card');
    const hiddenInput = wrapper.querySelector('input[name="id"]');
    const currentPrice = wrapper.querySelector('[id^="price-current-"]');
    const comparePrice = wrapper.querySelector('[id^="price-compare-"]');
    const saveBadge = wrapper.querySelector('[id^="price-save-"]');
    const addToCartBtn = wrapper.querySelector('[id^="add-to-cart-btn-"]');
    const form = wrapper.querySelector('form[action*="/cart/add"]');

    if (!variantButtons.length || !hiddenInput || !currentPrice) return;

    const updateVariant = (button) => {
      const variantId = button.dataset.variantId;
      const priceFormatted = button.dataset.priceFormatted;
      const comparePriceRaw = parseInt(button.dataset.comparePrice || '0', 10);
      const comparePriceFormatted = button.dataset.comparePriceFormatted || '';
      const available = button.dataset.available === 'true';
      const priceRaw = parseInt(button.dataset.price || '0', 10);

      if (variantId) {
        hiddenInput.value = variantId;
      }

      if (priceFormatted) {
        currentPrice.textContent = priceFormatted;
      }

      if (comparePrice && saveBadge) {
        if (comparePriceRaw > priceRaw && comparePriceFormatted !== '') {
          comparePrice.textContent = comparePriceFormatted;
          comparePrice.classList.remove('hidden');

          const savingsPercent = Math.round(((comparePriceRaw - priceRaw) * 100) / comparePriceRaw);
          saveBadge.textContent = 'SAVE ' + savingsPercent + '%';
          saveBadge.classList.remove('hidden');
        } else {
          comparePrice.textContent = '';
          comparePrice.classList.add('hidden');
          saveBadge.textContent = '';
          saveBadge.classList.add('hidden');
        }
      }

      if (addToCartBtn) {
        if (available) {
          addToCartBtn.disabled = false;
          addToCartBtn.innerHTML = `
            <span class="cart-icon">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M3 5h2l1.2 6h10.7l1.6-5H7.2" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"></path>
                <circle cx="10" cy="18" r="1.5" fill="currentColor"></circle>
                <circle cx="17" cy="18" r="1.5" fill="currentColor"></circle>
              </svg>
            </span>
            <span>Add to Cart</span>
          `;
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.innerHTML = `<span>Sold out</span>`;
        }
      }
    };

    variantButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;

        variantButtons.forEach((item) => item.classList.remove('is-active'));
        btn.classList.add('is-active');
        updateVariant(btn);
      });
    });

    if (form && !form.dataset.quickAddBound) {
      form.dataset.quickAddBound = 'true';

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const submitButton =
          form.querySelector('[name="add"]') ||
          form.querySelector('button[type="submit"]');

        try {
          if (submitButton) submitButton.disabled = true;

          const formData = new FormData(form);

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData,
            headers: {
              Accept: 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to add product to cart');
          }

          document.dispatchEvent(
            new CustomEvent(ThemeEvents.cartUpdate, {
              bubbles: true,
              detail: {
                source: 'quick-add',
                data: { didError: false },
              },
            })
          );
        } catch (error) {
          console.error('Quick add cart error:', error);
        } finally {
          if (submitButton && !submitButton.textContent?.toLowerCase().includes('sold out')) {
            submitButton.disabled = false;
          }
        }
      });
    }
  }

  /**
   * @param {VariantSelectedEvent} event
   */
  #updateQuickAddButtonState = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * @param {Element} modalContent
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const customVariantButtons = modalContent.querySelectorAll('.variant-card[data-variant-id]');
    if (customVariantButtons.length) {
      customVariantButtons.forEach((button) => {
        if (!(button instanceof HTMLElement)) return;

        const isMatch = button.dataset.variantId === selectedVariantId;
        button.classList.toggle('is-active', isMatch);

        if (isMatch) {
          button.dispatchEvent(new Event('click', { bubbles: true }));
        }
      });
      return;
    }

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * @param {CartUpdateEvent} event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}